import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("dochub_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !window.location.pathname.startsWith("/login")) {
      localStorage.removeItem("dochub_token");
      localStorage.removeItem("dochub_user");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  forgot: (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data),
  reset: (token, password) => api.post("/auth/reset-password", { token, password }).then((r) => r.data),
  getInvite: (token) => api.get(`/auth/invite/${token}`).then((r) => r.data),
  accept: (token, password, full_name) => api.post("/auth/accept-invite", { token, password, full_name }).then((r) => r.data),
  invite: (payload) => api.post("/auth/invite", payload).then((r) => r.data),
};

export const businessesApi = {
  list: () => api.get("/businesses").then((r) => r.data),
  get: (id) => api.get(`/businesses/${id}`).then((r) => r.data),
  updateSettings: (id, body) => api.patch(`/businesses/${id}/settings`, body).then((r) => r.data),
  branches: (id) => api.get(`/businesses/${id}/branches`).then((r) => r.data),
  addBranch: (id, payload) => api.post(`/businesses/${id}/branches`, payload).then((r) => r.data),
  updateBranch: (bizId, branchId, payload) => api.patch(`/businesses/${bizId}/branches/${branchId}`, payload).then((r) => r.data),
  deleteBranch: (bizId, branchId) => api.delete(`/businesses/${bizId}/branches/${branchId}`).then((r) => r.data),
  setBranchHidden: (bizId, branchId, hidden) => api.patch(`/businesses/${bizId}/branches/${branchId}/hidden`, null, { params: { hidden } }).then((r) => r.data),
};

export const documentsApi = {
  upload: (form) => api.post("/documents/upload", form).then((r) => r.data),
  list: (params) => api.get("/documents", { params }).then((r) => r.data),
  counts: (params) => api.get("/documents/counts", { params }).then((r) => r.data),
  get: (id) => api.get(`/documents/${id}`).then((r) => r.data),
  update: (id, body) => api.patch(`/documents/${id}`, body).then((r) => r.data),
  publish: (id) => api.post(`/documents/${id}/publish`).then((r) => r.data),
  assign: (id, business_id) => api.post(`/documents/${id}/assign-business`, { business_id }).then((r) => r.data),
  reassign: (id, business_id) => api.post(`/documents/${id}/reassign-business`, { document_id: id, business_id }).then((r) => r.data),
  markNotDup: (id) => api.post(`/documents/${id}/mark-not-duplicate`).then((r) => r.data),
  resetPublish: (id) => api.post(`/documents/${id}/reset-publish`).then((r) => r.data),
  bulk: (document_ids, action, status) => api.post("/documents/bulk", { document_ids, action, status }).then((r) => r.data),
  imageUrl: (id) => `${API_BASE}/documents/${id}/image?auth=${encodeURIComponent(localStorage.getItem("dochub_token") || "")}`,
  updateVendorRule: (id) => api.post(`/documents/${id}/update-vendor-rule`).then((r) => r.data),
  extractLineItems: (id) => api.post(`/documents/${id}/extract-line-items`).then((r) => r.data),
};

export const vendorsApi = {
  list: (business_id) => api.get("/vendors", { params: business_id ? { business_id } : {} }).then((r) => r.data),
  upsert: (payload, business_id) => api.post("/vendors", payload, { params: { business_id } }).then((r) => r.data),
  del: (id) => api.delete(`/vendors/${id}`).then((r) => r.data),
  syncFromAccounting: (business_id) => api.post("/vendors/sync-from-accounting", null, { params: { business_id } }).then((r) => r.data),
};

export const accountsApi = {
  list: (business_id, opts = {}) => api.get("/accounts", { params: { ...(business_id ? { business_id } : {}), ...(opts.include_hidden ? { include_hidden: true } : {}) } }).then((r) => r.data),
  syncFromAccounting: (business_id) => api.post("/accounts/sync-from-accounting", null, { params: { business_id } }).then((r) => r.data),
  setHidden: (id, hidden) => api.patch(`/accounts/${id}/hidden`, null, { params: { hidden } }).then((r) => r.data),
};

export const auditApi = {
  list: (params) => api.get("/audit-log", { params }).then((r) => r.data),
  actions: () => api.get("/audit-log/actions").then((r) => r.data),
};

export const inboundApi = {
  log: () => api.get("/inbound/log").then((r) => r.data),
};

export const vatApi = {
  list: (business_id, opts = {}) => api.get("/vat-codes", { params: { ...(business_id ? { business_id } : {}), ...(opts.include_hidden ? { include_hidden: true } : {}) } }).then((r) => r.data),
  upsert: (payload, business_id) => api.post("/vat-codes", payload, { params: { business_id } }).then((r) => r.data),
  del: (id) => api.delete(`/vat-codes/${id}`).then((r) => r.data),
  setHidden: (id, hidden) => api.patch(`/vat-codes/${id}/hidden`, null, { params: { hidden } }).then((r) => r.data),
};

export const integrationsApi = {
  status: () => api.get("/integrations/status").then((r) => r.data),
  authorize: (provider, business_id) => api.get(`/integrations/${provider}/authorize`, { params: { business_id } }).then((r) => r.data),
  setZohoOrg: (business_id, org_id) => api.post(`/integrations/${business_id}/zoho/org`, null, { params: { org_id } }).then((r) => r.data),
  disconnect: (business_id) => api.post(`/integrations/${business_id}/disconnect`).then((r) => r.data),
  trackingCategories: (business_id) => api.get(`/integrations/${business_id}/tracking-categories`).then((r) => r.data),
  masterSync: (business_id) => api.post("/integrations/master-sync", null, { params: { business_id } }).then((r) => r.data),
  openPurchaseOrders: (business_id, vendor_id) => api.get(`/integrations/${business_id}/open-purchase-orders`, { params: vendor_id ? { vendor_id } : {} }).then((r) => r.data),
};

export const usersApi = {
  list: (business_id) => api.get("/users", { params: business_id ? { business_id } : {} }).then((r) => r.data),
  update: (id, payload) => api.patch(`/users/${id}`, payload).then((r) => r.data),
  updateLinks: (id, payload) => api.patch(`/users/${id}/links`, payload).then((r) => r.data),
  deactivate: (id) => api.patch(`/users/${id}/deactivate`).then((r) => r.data),
  activate: (id) => api.patch(`/users/${id}/activate`).then((r) => r.data),
  del: (id) => api.delete(`/users/${id}`).then((r) => r.data),
};

export const stockApi = {
  history: (ingredient, business_id) => api.get("/v1/stock-prices/history", { params: { ingredient, business_id } }).then((r) => r.data),
  latest: (business_id) => api.get("/v1/stock-prices/latest", { params: business_id ? { business_id } : {} }).then((r) => r.data),
  ingredients: (business_id) => api.get("/v1/stock-prices/ingredients", { params: business_id ? { business_id } : {} }).then((r) => r.data),
  record: (payload) => api.post("/v1/stock-prices", payload).then((r) => r.data),
};

export const itemsApi = {
  list: (business_id) => api.get("/items", { params: business_id ? { business_id } : {} }).then((r) => r.data),
  upsert: (payload, business_id) => api.post("/items", payload, { params: { business_id } }).then((r) => r.data),
  del: (id) => api.delete(`/items/${id}`).then((r) => r.data),
  syncFromAccounting: (business_id) => api.post("/items/sync-from-accounting", null, { params: { business_id } }).then((r) => r.data),
};
